import { useMemo, useState, useCallback, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Package, Download, Upload, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import { format } from "date-fns";
import apiService from "../../services/apiService";
import { InvoiceViewSheet } from "../../components/purchaseOrder/InvoiceDetailDialog";
import { POStatusBadge } from "../../components/purchaseOrder/POStatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { cn } from "../../lib/utils";
import { Chip } from "../../components/ui/chip";
import type { Invoice } from "../../store/store";

const inrFmt = (n: number) => `₹${Math.round(n).toLocaleString("en-IN")}`;

const documentUploadStatus = (count?: number | string | null) =>
  Number(count ?? 0) > 0 ? "Uploaded" : "Pending";

function DocumentUploadStatusChip({ count }: { count?: number | string | null }) {
  const uploaded = documentUploadStatus(count) === "Uploaded";
  return (
    <Chip
      className={cn(
        "text-[10px] px-2 py-0.5",
        uploaded
          ? "bg-emerald-500/10 text-emerald-700"
          : "bg-blue-500/10 text-blue-700",
      )}
    >
      {uploaded ? "Uploaded" : "Pending"}
    </Chip>
  );
}

function normalizeEwayStatus(row: SupplierShipmentDetail): string {
  if (Number(row.eway_count ?? 0) > 0) return "uploaded";
  return String(row.e_way_status ?? row.eWayStatus ?? "")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .trim();
}

function formatEwayStatusLabel(status: string): string {
  return status
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const GST_STATUS_TONES = {
  green: "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-50 focus:ring-emerald-200",
  blue: "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-50 focus:ring-blue-200",
  red: "border-red-200 bg-red-50 text-red-700 hover:bg-red-50 focus:ring-red-200",
  yellow: "border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-50 focus:ring-amber-200",
  muted: "border-input bg-background text-muted-foreground",
} as const;

type GstStatusTone = keyof typeof GST_STATUS_TONES;

function getGstStatusTone(name?: string | null): GstStatusTone {
  const n = String(name ?? "")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!n) return "muted";
  if (n.includes("mismatch") || n.includes("mis match") || n.includes("failed")) return "red";
  if (n.includes("matched") || (n.includes("filed") && n.includes("match"))) return "green";
  if (n.includes("not filed") || n.includes("pending")) return "blue";
  if (n.includes("progress") || n.includes("partial") || n.includes("review")) return "yellow";
  if (n.includes("filed")) return "yellow";
  return "blue";
}

type AdminUser = { id: number; name: string };

type SupplierShipmentDetail = {
  purchase_order_id: string;
  supplier_id: string;
  vendor_name: string;
  asset_quantity: number;
  total_asset_value: number;
  delivery_state?: string;
  delivery_address?: string;
  created_at?: string;
  assetTotalValue?: number;
  shipmentAssetsValue_invoice?: number;
  shipmentAssetsValue_eway?: number;
  shipmentAssetsValue_gstr?: number;
  taxinvoice_count?: number;
  eway_count?: number;
  e_way_status?: string;
  eWayStatus?: string;
  id: number;
  poNumber?: string;
  number?: string;
  vendorName?: string;
  amount?: number;
  tax?: number;
  date?: string;
  dueDate?: string;
  due_date?: string;
  status?: string;
  tds_amount?: number;
  tds_exempted?: number;
  previousPurchaseValue?: number;
  attachmentUrl?: string;
  paymentProofUrl?: string;
  taxInvoiceUrl?: string;
  total_paid?: number;
  invoiceGstStatus?: string;
  invoiceGstId?: number | string;
  po_id?: string;
};

type InvoiceGstStatusMaster = {
  id: number | string;
  name: string;
};

interface Props {
  rsId: string;
  noaId: string;
  customerName: string;
  customerId: string;
  onShipmentCreated?: () => void | Promise<void>;
}

function buildFallbackInvoice(row: SupplierShipmentDetail, poId?: string | number): Invoice {
  return {
    id: String(row.id),
    poId: String(poId ?? row.purchase_order_id ?? ""),
    number: row.number ?? "",
    date: row.date ?? row.created_at ?? new Date().toISOString(),
    dueDate: row.dueDate ?? row.due_date,
    amount: Number(row.amount ?? row.total_asset_value ?? 0),
    tax: Number(row.tax ?? 0),
    status: (row.status as Invoice["status"]) || "pending",
    previousPurchaseValue: row.previousPurchaseValue,
    tds_amount: row.tds_amount,
    tds_exempted: row.tds_exempted,
    attachmentUrl: row.attachmentUrl,
    paymentProofUrl: row.paymentProofUrl,
    taxInvoiceUrl: row.taxInvoiceUrl,
    e_way_status: row.e_way_status ?? row.eWayStatus ?? null,
  };
}

export default function SupplierShipmentsSection({ rsId, noaId, customerId, onShipmentCreated }: Props) {
  const [invoiceDetails, setInvoiceDetails] = useState<SupplierShipmentDetail[]>([]);
  const [loadingShipments, setLoadingShipments] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [assetTotalValue, setAssetTotalValue] = useState(0);
  const [shipmentAssetsValue_invoice, setshipmentAssetsValue_invoice] = useState(0);
  const [shipmentAssetsValue_eway, setshipmentAssetsValue_eway] = useState(0);
  const [shipmentAssetsValue_gstr, setshipmentAssetsValue_gstr] = useState(0);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [selectedPo, setSelectedPo] = useState<any>(null);
  const [openingInvoiceId, setOpeningInvoiceId] = useState<number | null>(null);
  const [adminUserList, setAdminUserList] = useState<AdminUser[]>([]);
  const [gstStatusOptions, setGstStatusOptions] = useState<InvoiceGstStatusMaster[]>([]);
  const [updatingGstInvoiceId, setUpdatingGstInvoiceId] = useState<number | null>(null);
  const [updatingEwayInvoiceId, setUpdatingEwayInvoiceId] = useState<number | null>(null);

  const loadShipmentDetails = useCallback(async (options?: { silent?: boolean }) => {
    if (!noaId) {
      setInvoiceDetails([]);
      setAssetTotalValue(0);
      setshipmentAssetsValue_invoice(0);
      setshipmentAssetsValue_eway(0);
      setshipmentAssetsValue_gstr(0);
      return;
    }
    if (!options?.silent) setLoadingShipments(true);
    try {
      const res = await apiService.get<{ data?: { invoice_details?: SupplierShipmentDetail[], asset_details?: any[], assetTotalValue?: number, shipmentAssetsValue_invoice?: number, shipmentAssetsValue_eway?: number, shipmentAssetsValue_gstr?: number }; invoice_details?: SupplierShipmentDetail[]; assetTotalValue?: number, shipmentAssetsValue_invoice?: number, shipmentAssetsValue_eway?: number, shipmentAssetsValue_gstr?: number }>(
        `/disbursement/get-invoice-assets/${encodeURIComponent(noaId)}/${encodeURIComponent(rsId)}`
      );
      setInvoiceDetails(res?.data?.invoice_details ?? res?.invoice_details ?? []);
      setAssetTotalValue(Number(res?.data?.assetTotalValue ?? res?.assetTotalValue ?? 0));
      setshipmentAssetsValue_invoice(Number(res?.data?.shipmentAssetsValue_invoice ?? res?.shipmentAssetsValue_invoice ?? 0));
      setshipmentAssetsValue_eway(Number(res?.data?.shipmentAssetsValue_eway ?? res?.shipmentAssetsValue_eway ?? 0));
      setshipmentAssetsValue_gstr(Number(res?.data?.shipmentAssetsValue_gstr ?? res?.shipmentAssetsValue_gstr ?? 0));
    } catch (error: any) {
      console.error("Failed to load shipments:", error);
      if (!options?.silent) {
        toast.error(error?.message || "Failed to load shipments");
        setInvoiceDetails([]);
        setAssetTotalValue(0);
        setshipmentAssetsValue_invoice(0);
        setshipmentAssetsValue_eway(0);
        setshipmentAssetsValue_gstr(0);
      }
    } finally {
      if (!options?.silent) setLoadingShipments(false);
    }
  }, [noaId, rsId]);

  useEffect(() => {
    void loadShipmentDetails();
  }, [loadShipmentDetails]);

  useEffect(() => {
    const fetchAdminUserList = async () => {
      try {
        const response = await apiService.get<{ data: AdminUser[] }>(`/customers/get-admin-user-list`);
        setAdminUserList(
          (response.data || []).map((doc) => ({ id: doc.id, name: doc.name })),
        );
      } catch (error) {
        console.error("Error fetching admin users:", error);
      }
    };
    void fetchAdminUserList();
  }, []);

  useEffect(() => {
    const fetchGstStatusMaster = async () => {
      try {
        const response = await apiService.get<any>(`/disbursement/get-invoice-status-master`);
        const raw = response?.data ?? response;
        const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
        setGstStatusOptions(
          list
            .map((item: any) => ({
              id: item.id ?? item.invoiceGstId,
              name: String(item.name ?? item.status ?? item.invoiceGstStatus ?? "").trim(),
            }))
            .filter((item: InvoiceGstStatusMaster) => item.id != null && item.name),
        );
      } catch (error) {
        console.error("Failed to load invoice GST status master:", error);
      }
    };
    void fetchGstStatusMaster();
  }, []);

  const getGstStatusValue = useCallback((row: SupplierShipmentDetail) => {
    if (row.invoiceGstId != null && String(row.invoiceGstId).trim() !== "") {
      return String(row.invoiceGstId);
    }
    const match = gstStatusOptions.find(
      (opt) => opt.name.toLowerCase() === String(row.invoiceGstStatus ?? "").trim().toLowerCase(),
    );
    return match ? String(match.id) : "";
  }, [gstStatusOptions]);

  const handleGstStatusChange = useCallback(async (row: SupplierShipmentDetail, invoiceGstId: string) => {
    if (!invoiceGstId || getGstStatusValue(row) === invoiceGstId) return;
    setUpdatingGstInvoiceId(row.id);
    const selected = gstStatusOptions.find((opt) => String(opt.id) === invoiceGstId);
    try {
      await apiService.post("/disbursement/update-invoice-gst-status", {
        invoiceId: row.id,
        invoiceGstId: Number.isNaN(Number(invoiceGstId)) ? invoiceGstId : Number(invoiceGstId),
      });
      setInvoiceDetails((prev) =>
        prev.map((item) =>
          item.id === row.id
            ? { ...item, invoiceGstId, invoiceGstStatus: selected?.name ?? item.invoiceGstStatus }
            : item,
        ),
      );
      toast.success("Vendor GSTR-1 status updated");
      await loadShipmentDetails({ silent: true });
      await onShipmentCreated?.();
    } catch (error: any) {
      console.error("Failed to update invoice GST status:", error);
      toast.error(error?.message || "Failed to update Vendor GSTR-1 status");
    } finally {
      setUpdatingGstInvoiceId(null);
    }
  }, [getGstStatusValue, gstStatusOptions, loadShipmentDetails, onShipmentCreated]);

  const handleEwayStatusChange = useCallback(async (row: SupplierShipmentDetail, eWayStatus: string) => {
    if (eWayStatus !== "Not Applicable") return;
    setUpdatingEwayInvoiceId(row.id);
    try {
      await apiService.post("/disbursement/update-invoice-eway-status", {
        invoice_id: row.id,
        e_way_status: "Not Applicable",
      });
      setInvoiceDetails((prev) =>
        prev.map((item) =>
          item.id === row.id ? { ...item, e_way_status: "Not Applicable" } : item,
        ),
      );
      toast.success("E-Way status updated");
      await loadShipmentDetails({ silent: true });
      await onShipmentCreated?.();
    } catch (error: any) {
      console.error("Failed to update E-Way status:", error);
      toast.error(error?.message || "Failed to update E-Way status");
    } finally {
      setUpdatingEwayInvoiceId(null);
    }
  }, [loadShipmentDetails, onShipmentCreated]);

  const handleRefreshAfterSheetAction = useCallback(async () => {
    await loadShipmentDetails({ silent: true });
    await onShipmentCreated?.();
  }, [loadShipmentDetails, onShipmentCreated]);

  const handleOpenManage = useCallback(async (row: SupplierShipmentDetail) => {
    setOpeningInvoiceId(row.id);
    try {
      const poRes = await apiService.get<any>(
        `/purchase-order/get-purchase-order-by-invoiced-id/${row.po_id}`,
      );
      const po = poRes?.data ?? poRes;
      if (!po) {
        toast.error("Failed to fetch purchase order for this invoice");
        return;
      }

      let invoice: Invoice | null = null;
      const poId = po.id ?? po.poId ?? row.purchase_order_id;
      if (poId) {
        try {
          const invRes = await apiService.get<any>(`/purchase-order/get-invoices/${poId}`);
          const list = Array.isArray(invRes?.data) ? invRes.data : Array.isArray(invRes) ? invRes : [];
          const match = list.find((inv: any) => String(inv.id) === String(row.id));
          if (match) {
            invoice = {
              id: String(match.id),
              poId: String(match.poId ?? match.po_id ?? poId),
              number: match.number ?? row.number ?? "",
              date: match.date ?? row.date ?? row.created_at ?? new Date().toISOString(),
              dueDate: match.dueDate ?? match.due_date,
              amount: Number(match.amount ?? 0),
              tax: Number(match.tax ?? 0),
              status: match.status || "pending",
              previousPurchaseValue: match.previousPurchaseValue,
              tds_amount: match.tds_amount,
              tds_exempted: match.tds_exempted,
              attachmentUrl: match.attachmentUrl,
              paymentProofUrl: match.paymentProofUrl,
              taxInvoiceUrl: match.taxInvoiceUrl,
              assetLineIds: match.assetLineIds,
              e_way_status: match.e_way_status ?? match.eWayStatus ?? row.e_way_status ?? row.eWayStatus ?? null,
            };
          }
        } catch (error) {
          console.error("Failed to load invoice details:", error);
        }
      }

      if (!invoice) {
        invoice = buildFallbackInvoice(row, poId);
      }

      setSelectedInvoice(invoice);
      setSelectedPo({
        ...po,
        customerId: po.customerId ?? po.customer_id ?? customerId,
        supplierId: po.supplierId ?? po.supplier_id ?? row.supplier_id,
        purchaseOrderCode: po.purchaseOrderCode ?? po.purchase_order_code ?? row.poNumber,
      });
      setSheetOpen(true);
    } catch (error: any) {
      console.error("Failed to open invoice:", error);
      toast.error(error?.message || "Failed to open invoice details");
    } finally {
      setOpeningInvoiceId(null);
    }
  }, [customerId]);

  const coverage = useMemo(() => {
    const invoicePct = assetTotalValue > 0 ? Math.round((shipmentAssetsValue_invoice / assetTotalValue) * 100) : 0;

    const ewayCovered = shipmentAssetsValue_eway;
    const ewayPct = assetTotalValue > 0 ? Math.round((ewayCovered / assetTotalValue) * 100) : 0;
    const gstMatchedPct = assetTotalValue > 0 ? Math.round((shipmentAssetsValue_gstr / assetTotalValue) * 100) : 0;
    return { invoicePct, ewayPct, ewayCovered, gstMatchedPct };
  }, [assetTotalValue, shipmentAssetsValue_invoice, shipmentAssetsValue_eway, shipmentAssetsValue_gstr, invoiceDetails]);

  const sortedInvoiceDetails = useMemo(
    () =>
      [...invoiceDetails].sort((a, b) =>
        String(a.vendorName ?? a.vendor_name ?? "").localeCompare(
          String(b.vendorName ?? b.vendor_name ?? ""),
          undefined,
          { sensitivity: "base" },
        ),
      ),
    [invoiceDetails],
  );

  const handleExport = async () => {
    if (downloadingExcel) return;
    setDownloadingExcel(true);
    try {
      const blob = await apiService.post<Blob>(
        "/disbursement/download-post-disbursement-excel",
        { noa_id: noaId, rsId },
        { responseType: "blob" }
      );

      const fileName = `${rsId}-Shipments-${format(new Date(), "yyyyMMdd-HHmm")}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel exported");
    } catch (error: any) {
      console.error("Failed to export Excel:", error);
      toast.error(error?.message || "Failed to export Excel");
    } finally {
      setDownloadingExcel(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2"><Package className="h-5 w-5" />Purchase Invoices</CardTitle>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-1" onClick={handleExport} disabled={downloadingExcel}>
            {downloadingExcel ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-3 grid grid-cols-3 gap-3">
          <CoverageBar label="Tax Invoice coverage" pct={coverage.invoicePct} covered={shipmentAssetsValue_invoice} total={assetTotalValue} />
          <CoverageBar label="E-Way Bill coverage" pct={coverage.ewayPct} covered={coverage.ewayCovered} total={assetTotalValue} />
          <CoverageBar label="Vendor GSTR-1 Matched" pct={coverage.gstMatchedPct} covered={shipmentAssetsValue_gstr} total={assetTotalValue} />
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice Number</TableHead>
              <TableHead>PO ID</TableHead>
              <TableHead>Vendor</TableHead>
              
              <TableHead className="text-right">Quantity</TableHead>
              <TableHead className="text-right">Asset Value</TableHead>
              <TableHead>Paid Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Vendor GSTR-1</TableHead>
              <TableHead>Tax Invoice</TableHead>
              <TableHead>E-Way Bill</TableHead>
              <TableHead className="text-center">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loadingShipments && (
              <TableRow>
                <TableCell colSpan={11} className="text-center text-muted-foreground py-8">
                  Loading invoices…
                </TableCell>
              </TableRow>
            )}
            {!loadingShipments && sortedInvoiceDetails.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} className="text-center text-muted-foreground py-8">
                  No Invoices yet.
                </TableCell>
              </TableRow>
            )}
            {!loadingShipments && sortedInvoiceDetails.map(s => (
              <TableRow key={s.id}>
                <TableCell className={cn("font-medium text-xs", !String(s.number ?? "").trim() && "text-center tracking-widest")}>
                  {String(s.number ?? "").trim() || "—"}
                </TableCell>
                <TableCell className="text-sm font-mono">{s.poNumber}</TableCell>
                <TableCell className="text-sm">{s.vendorName || s.vendor_name || "-"}</TableCell>
                <TableCell className="text-right tabular-nums text-sm">{s.asset_quantity}</TableCell>
                <TableCell className="text-right tabular-nums">{inrFmt(s.total_asset_value)}</TableCell>
                <TableCell className="text-sm">{inrFmt(Number(s.total_paid ?? 0))}</TableCell>
                <TableCell>
                  <POStatusBadge status={s.status as any} />
                </TableCell>
                <TableCell>
                  {(() => {
                    const selectedOpt = gstStatusOptions.find(
                      (opt) => String(opt.id) === getGstStatusValue(s),
                    );
                    const statusLabel = selectedOpt?.name ?? s.invoiceGstStatus ?? "";
                    const tone = getGstStatusTone(statusLabel);
                    return (
                      <Select
                        value={getGstStatusValue(s) || undefined}
                        onValueChange={(value) => void handleGstStatusChange(s, value)}
                        disabled={updatingGstInvoiceId === s.id}
                      >
                        <SelectTrigger
                          className={cn(
                            "h-8 min-w-[140px] text-xs font-medium",
                            GST_STATUS_TONES[tone],
                          )}
                        >
                          {updatingGstInvoiceId === s.id ? (
                            <span className="flex items-center gap-1">
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />Updating…
                            </span>
                          ) : (
                            <SelectValue placeholder={s.invoiceGstStatus || "Select status"} />
                          )}
                        </SelectTrigger>
                        <SelectContent>
                          {gstStatusOptions.map((opt) => {
                            const itemTone = getGstStatusTone(opt.name);
                            return (
                              <SelectItem
                                key={String(opt.id)}
                                value={String(opt.id)}
                                className={cn(
                                  "text-xs font-medium my-0.5 rounded-md",
                                  itemTone === "green" && "bg-emerald-50 text-emerald-700 focus:bg-emerald-100 focus:text-emerald-800",
                                  itemTone === "blue" && "bg-blue-50 text-blue-700 focus:bg-blue-100 focus:text-blue-800",
                                  itemTone === "red" && "bg-red-50 text-red-700 focus:bg-red-100 focus:text-red-800",
                                  itemTone === "yellow" && "bg-amber-50 text-amber-800 focus:bg-amber-100 focus:text-amber-900",
                                )}
                              >
                                {opt.name}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  <DocumentUploadStatusChip count={s.taxinvoice_count} />
                </TableCell>
                <TableCell>
                  {(() => {
                    const ewayStatus = normalizeEwayStatus(s);
                    const isUpdatingEway = updatingEwayInvoiceId === s.id;

                    if (ewayStatus === "uploaded") {
                      return (
                        <Chip className="text-[10px] px-2 py-0.5 bg-emerald-500/10 text-emerald-700">
                          Uploaded
                        </Chip>
                      );
                    }

                    if (ewayStatus === "not applicable") {
  return (
                        <span className="text-sm text-slate-700">Not Applicable</span>
                      );
                    }

                    if (ewayStatus && ewayStatus !== "pending") {
                      return (
                        <span className="text-sm text-slate-700">
                          {formatEwayStatusLabel(ewayStatus)}
                        </span>
                      );
                    }

  return (
                      <Select
                        disabled={isUpdatingEway}
                        onValueChange={(value) => void handleEwayStatusChange(s, value)}
                      >
                        <SelectTrigger className="h-8 min-w-[150px] border-blue-200 bg-blue-50 px-3 text-xs font-medium text-blue-700 hover:bg-blue-50 focus:ring-blue-200">
                          {isUpdatingEway ? (
                            <span className="flex items-center gap-1">
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />Updating…
                            </span>
                          ) : (
                            <SelectValue placeholder="Pending" />
                          )}
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Not Applicable">Not Applicable</SelectItem>
                        </SelectContent>
                      </Select>
                    );
                  })()}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    disabled={openingInvoiceId === s.id}
                    onClick={() => void handleOpenManage(s)}
                  >
                    {openingInvoiceId === s.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Upload className="h-3.5 w-3.5" />
                    )}
                    Manage
                  </Button>
                          </TableCell>
                        </TableRow>
            ))}
                    </TableBody>
                  </Table>
      </CardContent>

      {selectedInvoice && (
        <InvoiceViewSheet
          invoice={selectedInvoice}
          po={selectedPo}
          open={sheetOpen}
          onOpenChange={(open) => {
            setSheetOpen(open);
            if (!open) {
              setSelectedInvoice(null);
              setSelectedPo(null);
            }
          }}
          addPayment={() => { void handleRefreshAfterSheetAction(); }}
          adminUserList={adminUserList}
        />
      )}
    </Card>
  );
}

function CoverageBar({ label, pct, covered, total }: { label: string; pct: number; covered: number; total: number }) {
  const clamped = Math.min(100, Math.max(0, Number(pct) || 0));
  return (
    <div className="rounded-lg border p-3">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium tabular-nums">{clamped}%</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#e5e7eb]">
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${clamped}%`,
            backgroundColor: "hsl(43, 75.90%, 39.00%)",
          }}
        />
    </div>
      <div className="mt-1 text-[11px] text-muted-foreground tabular-nums">{inrFmt(covered)} / {inrFmt(total)}</div>
    </div>
  );
}
